import { CssBaseline, ThemeProvider as MuiThemeProvider, Theme, ThemeOptions, createTheme } from '@mui/material';
import { FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import ThemeContext from '../../../contexts/styles/ThemeContext';
import { getDefaultAdminThemeOptions } from '../../../styles/defaults/DefaultAdminThemeOptions';
import { DefaultSpeciesThemes } from '../../../styles/defaults/DefaultSpeciesThemeOptions';

interface IDefaultAdminThemeProviderProps extends PropsWithChildren {
    applySpeciesThemes?: boolean;
    themeOptions?: ThemeOptions;
    beforeThemeCreated?: (themeOptions: ThemeOptions) => ThemeOptions;
    afterThemeCreated?: (theme: Theme) => Theme;
}

const DefaultAdminThemeProvider: FC<IDefaultAdminThemeProviderProps> = ({
    afterThemeCreated,
    applySpeciesThemes,
    beforeThemeCreated,
    themeOptions, 
    children 
}) => {

    const [theme, setTheme] = useState<'light' | 'dark'>('dark');

    const { species: { species } } = useContext(SpeciesContext);
    
    const muiTheme = useMemo(() => {
        let options: ThemeOptions = getDefaultAdminThemeOptions(theme);
        // Apply species themes

        if (applySpeciesThemes) {
            const currentTheme = DefaultSpeciesThemes[species];
    
            options.palette = {
                ...options.palette,
                primary: {
                    main: currentTheme.primaryColor
                },
                secondary: {
                    main: currentTheme.secondaryColor
                },
                background: {
                    default: currentTheme.backgroundColor
                }
            };
        }

        // Update MUI Theme Options using event function if provided.
        if (beforeThemeCreated) {
            options = beforeThemeCreated(options);
        }

        // Create MUI Theme from MUI Theme Options.
        let t = createTheme(options);

        // Update MUI Theme using event function if provided.
        if (afterThemeCreated) {
            t = afterThemeCreated(t);
        }

        return t;
    }, [theme, themeOptions, afterThemeCreated, beforeThemeCreated, species]);

    const toggleTheme = useCallback(() => {
        setTheme(c => c === 'light' ? 'dark' : 'light')
    }, [setTheme])

    const themeContext = useMemo(() => ({
        theme, 
        toggleTheme
    }), [theme, toggleTheme]);

    return (
        <ThemeContext.Provider value={themeContext}>
            <MuiThemeProvider theme={muiTheme}>
                <CssBaseline enableColorScheme />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
}

export default DefaultAdminThemeProvider;